import clsx from "clsx";
import dayjs from "dayjs";
import sample from "lodash/sample";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import { Seomatic } from "react-seomatic";

import BlogLayoutNewsletterForm from "@components/BlogLayoutNewsletterForm";
import ColumnLayout from "@components/ColumnLayout";
import ContentSections from "@components/ContentSections";
import EntryList from "@components/EntryList";
import PageSections from "@components/PageSections";
import useSetHeaderTheme from "@hooks/useSetHeaderTheme";
import { PreviewContent } from "@interfaces/PreviewContent.interface";
import { returnStaticProps } from "@lib/returnStaticProps";
import craft from "@pages/api/craft";
import QueryGlossary from "@queries/glossary/QueryGlossary.graphql";
import QuerySlugs from "@queries/QuerySlugs.graphql";

import DotBorder from "@components/DotBorder";
import Spacer from "@components/Spacer";
import Link from "next/link";
import Script from "next/script";

export const getStaticPaths: GetStaticPaths = async (context) => {
	// Fetch every possible path
	const response = await craft(QuerySlugs, {
		section: "glossary",
		limit: null,
	});

	const paths = [];

	response.entries.map((item) => {
		console.log(`Generating /glossary/${item.slug}`);

		paths.push({
			params: { slug: item.slug },
		});
	});

	return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps = async ({
	params,
	previewData: nextPreviewData,
	preview,
}) => {
	// For Craft preview capability
	const previewData: PreviewContent = nextPreviewData as PreviewContent;

	// Construct uri from the slug array
	const { slug } = params;

	// What is the uri
	const uri = `/glossary/${slug}`;

	// Fetch the page's data
	const response = await craft(
		QueryGlossary,
		{ slug },
		preview ? previewData.token : null
	);

	const {
		pageEntry,
		item,
		primaryNavigation,
		primaryCallToActions,
		globalSet,
	} = response;

	if (!item) {
		return {
			notFound: true,
		};
	}

	let relatedAttacks = [];

	// Fetch the page's data
	if (item.attackTypes.length) {
		const relatedResponse = await craft(
			QueryGlossary,
			{ attackCategorySlug: item.attackTypes[0]?.slug, relatedLimit: 3 },
			preview ? previewData.token : null
		);

		({ relatedAttacks } = relatedResponse);
	}

	// Return props if entry exists, otherwise check Retour for redirects or 404
	const returnResponse = await returnStaticProps(
		{
			navigation: {
				primaryNavigation,
				primaryCallToActions,
			},
			pageEntry,
			globalSet,
			item,
			relatedAttacks,
		},
		!item,
		uri
	);

	return returnResponse;
};

interface IProps {
	pageEntry: any;
	item: any;
	relatedAttacks: any[];
}

export default function Page({
	pageEntry,
	item,
	relatedAttacks,
}: IProps): JSX.Element {
	useSetHeaderTheme(pageEntry.headerTheme);

	const faqs = [];
	let faqSchema = {};

	let formData;

	if (item.faqSchema?.length) {
		item.faqSchema.map((item) => {
			faqs.push({
				"@type": "Question",
				name: item.question,
				acceptedAnswer: {
					"@type": "Answer",
					text: item.answer,
				},
			});
		});

		faqSchema = {
			"@context": "https://schema.org",
			"@type": "FAQPage",
			mainEntity: faqs,
		};
	}

	return (
		<>
			<Seomatic Head={Head} {...item.seomatic} />

			{item.faqSchema?.length > 0 && (
				<Script
					type="application/ld+json"
					id="faqSchema"
					dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
				/>
			)}

			<div className={clsx("Attack__Title-container mx-auto lg:p-15 relative")}>
				<div
					className={clsx(
						"bg-white-01 pt-10 pb-10 px-6 max-w-screen-xl mx-auto  box-sizing relative"
					)}
				>
					<Link
						href="/glossary"
						className="Attack__return font-medium border-b-[3px] border-purple-01 pb-1"
					>
						Back to Attack Glossary
					</Link>

					<h1 className={clsx("text-5xl font-medium mt-12")}>{item.title}</h1>

					<DotBorder />
				</div>
			</div>

			<div className={clsx("relative", "py-15 bg-white-01")}>
				<ColumnLayout>
					<ColumnLayout.MainColumn>
						<div className={clsx("pb-10")}>
							<Spacer horizontalRule size="none" />
						</div>
						{!!item.contentSections && (
							<ContentSections sections={item.contentSections} />
						)}
					</ColumnLayout.MainColumn>

					<ColumnLayout.SidebarColumn className={clsx("lg:pb-15")}>
						{!!relatedAttacks.length && (
							<div className={clsx("bg-white-02 px-10 py-10 mb-15")}>
								<EntryList
									heading={`Recent Examples of ${item.title} Attacks`}
									entries={relatedAttacks}
									hideCategory={true}
									hideSubject={true}
                  hideDate={true}
                  hideImage={true}
									borderColor={"#FED141"}
									showReadMore={true}
									readMoreText={"See Attack Details"}
									buttonArrow={"left"}
									hrPosition={"top"}
								/>
							</div>
						)}

						<BlogLayoutNewsletterForm
							formHeading={pageEntry.formHeading}
							formDescription={pageEntry.formDescription}
							form={pageEntry?.form?.[0]}
							formData={formData}
							successMessage={pageEntry.successMessage}
						/>
					</ColumnLayout.SidebarColumn>
				</ColumnLayout>
				<DotBorder topOnly />
			</div>

			{pageEntry.footerModule?.length > 0 && (
				<PageSections
					sections={pageEntry.footerModule[0].pageSections}
					isPageModule
				/>
			)}
		</>
	);
}
